import React, { useContext, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Helmet } from 'react-helmet';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LayerGroupIcon from '../components/LayerGroupIcon';
import { Link } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import MasterLayout from '../components/Layout';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import { logIn } from '../utility/index.js';
import strings from '../utility/strings.js';
import { useAppState } from '../../index.js';
import { visuallyHidden } from '@mui/utils';

const FavoritesLink = React.forwardRef((props, ref) => <Link to="/favorites/" {...props} ref={ref} />);
const UpcomingLink = React.forwardRef((props, ref) => <Link to="/favorites/upcoming/" {...props} ref={ref} />);
var random = Math.floor(Math.random() * 7)

const REMOVE_FAVORITE = gql`
  mutation RemoveFavorite ($favoriteId: Guid!) {
    removeFavoriteByFavoriteId (favoriteId: $favoriteId) {
      id
    }
  } 
`;

const Favorites = (props) => {
  const { locations, currentUser, GET_USER } = useAppState();
  const { AppContext } = props;
  return (
    <>
      <Helmet>
        <title>Manage Favorites - Purdue Dining Menus</title>
        <meta name="description" content="Manage your favorite menu items at Purdue University dining locations. Save and track your preferred dishes." />
        <meta name="keywords" content="favorite foods, Purdue dining favorites, menu preferences, saved items" />
        <meta property="og:title" content="Manage Favorites - Purdue Dining" />
        <meta property="og:description" content="Manage your favorite menu items at Purdue University dining locations." />
      </Helmet>
      <MasterLayout AppContext={AppContext} locations={locations} currentUser={currentUser} GET_USER={GET_USER} pageName="Manage Favorites">
        <FavoritesView AppContext={AppContext} locations={locations} currentUser={currentUser} GET_USER={GET_USER} />
      </MasterLayout>
    </>
  );
}

function descendingComparator(a, b, orderBy) {
  if (b.item.name < a.item.name) {
    return -1;
  }
  if (b.item.name > a.item.name) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  if (array !== undefined) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[0].item.name - b[0].item.name;
    });
    return stabilizedThis.map((el) => el[0]);
  } else {
    return [];
  }
}

const FavoritesView = (props) => {
  const { AppContext, currentUser, GET_USER } = props;
  const { styleprops } = useContext(AppContext);
  const value = 0;
  const { favorites } = currentUser;
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name'); // Fixed: restored setOrderBy
  const [page, setPage] = useState(0);
  const [dense] = useState(true); // Currently unused but kept for future table density
  const [rowsPerPage, setRowsPerPage] = useState(15);

  function renderDietaryTags(fav) {
    if (fav.traits) {
      return (
        <div className="favorite-dietary-tags" > {
          fav.traits.map((x, i) => {
            return <img key={x.name + "_" + i} className="favorite-dietary-tags__icon" src={x.svgIconWithoutBackground} width="25" height="25" alt={x.name} title={x.name} />
          })
        }</div>
      );
    } else {
      return null;
    }
  }

  function EnhancedTableHead(props) {
    const { order, onRequestSort } =
      props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          <TableCell
            align={'left'}
            padding={'normal'}
            sortDirection={order}
          >
            <TableSortLabel
              active={true}
              direction={order}
              onClick={createSortHandler('item.name')}
            >
              <Typography variant='body1' component='span' sx={{ fontWeight: 'bold' }}>Item Name</Typography>
              <Box component="span" sx={visuallyHidden}>
                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
              </Box>
            </TableSortLabel>
          </TableCell>
          <TableCell
            align={'left'}
            padding={'normal'}
            sortDirection={order}
          >
            <Typography variant='body1' component='span' sx={{ fontWeight: 'bold' }}>Dietary Traits</Typography>
          </TableCell>
          <TableCell
            align={'right'}
            padding={'normal'}
            sortDirection={order}
          >
            <Typography variant='body1' component='span' sx={{ fontWeight: 'bold' }}>Actions</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - favorites.length) : 0;

  const visibleRows =
    stableSort(favorites, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

  const [removeFavoriteByFavoriteId] = useMutation(REMOVE_FAVORITE, {
    refetchQueries: [
      GET_USER,
      'getCurrentUser'
    ]
  });

  function removeFavorite(id) {
    removeFavoriteByFavoriteId({ variables: { favoriteId: id } })
  }

  const renderFavorites = () => {
    return <Paper elevation={6} className="favorite-table__container">
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
        >
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;
              return (
                <ControlledTableRow
                  key={labelId}
                  removeFavorite={removeFavorite}
                  renderDietaryTags={renderDietaryTags}
                  labelId={labelId}
                  favoriteId={row.favoriteId}
                  item={row.item}
                />
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={favorites.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  }

  const ControlledTableRow = (props) => {
    // This component is to control the visibility of table rows that may be hidden by the user, but the request is still being processed by graphQL
    // Seems that it doesn't quite work as expected? maybe needs to have a pop up for loading when an action happens?
    const { favoriteId, item, labelId, removeFavorite, renderDietaryTags } = props;
    const [visible, setVisible] = useState(true);

    function handleRemove(favoriteId) {
      setVisible(false);
      removeFavorite(favoriteId);
    }

    if (visible) {
      return (
        <TableRow
          hover
          tabIndex={-1}
        // sx={{ cursor: 'pointer' }}
        >
          <TableCell
            component="th"
            id={labelId}
            scope="row"
          >
            <Typography>{item.name}</Typography>
            {/* {renderDietaryTags(item)} */}
          </TableCell>
          <TableCell align="left">
            {item.components && item.components.length > 0 ?
              <div className="station-item-traits"><LayerGroupIcon /> Collection</div> : <>
                {renderDietaryTags(item)}
                {item.isDiscontinued && <span className="badge bg-md-gray-300 c-favorite-item--badge" style={{ color: "#000", marginRight: 15 }}>Discontinued</span>}
                {item.isNutritionReady === false && <span className="badge bg-md-gray-300 c-favorite-item--badge" style={{ color: '#000' }}>Allergens Unavailable</span>}
              </>}
          </TableCell>
          <TableCell align="right">
            <Link to={"/item/" + item.itemId} state={{ lastString: `Return to User Favorites`, lastPath: `/favorites/` }}><Button variant="outlined">View Item</Button></Link>
            <Button variant="contained" onClick={() => handleRemove(favoriteId)}>Remove</Button>
          </TableCell>
        </TableRow>
      )
    } else {
      return
    }
  }



  const renderBlank = (day, currentUser) => {
    let loggedIn = currentUser !== "Guest" ? true : false;
    let message = (loggedIn ? strings.nullfavorites : strings.loginfavorites);
    let icon = (loggedIn ? <WarningIcon /> : <ErrorOutlineIcon />);
    let status = (loggedIn ? '' : 'error');

    return (
      <div className="login_message" >
        <div className={"message " + status} onClick={loggedIn ? null : () => logIn()}><div className="icon">{icon}</div><div className="text">{message}</div></div>
        {loggedIn ? null : <span className="userpanel-account-link null-login-link" onClick={() => logIn()}><LockIcon /><span>Login</span></span>}
      </div>
    )

  }

  return <main className={"mdl-layout__content"} style={{ ...styleprops, paddingTop: 68 }}>
    <div className="mdl-layout__content-page" id="main">
      <AppBar position="static">
        <Tabs classes={{ root: "favorites__tabbar", indicator: "favorites__tabbar--indicator" }} value={value}>
          <Tab classes={{ wrapper: "favorites__tabbar--tab" }} label="Favorite Items" component={FavoritesLink} />
          <Tab classes={{ wrapper: "favorites__tabbar--tab" }} label="Upcoming Favorites" component={UpcomingLink} />
        </Tabs>
      </AppBar>
      {currentUser !== "Guest" && value === 0 ?

        <div className="favorites__description">
          <div className="favorites__description__content">
            <h2 className="favorites__description__content--title">Your Favorite Menu Items</h2>

            <div className="favorites__description__content--text">
              <p>Select favorite menu items by clicking the star next to item names. Menu items that you favorite will appear in the list below. Clicking <em>Remove</em> will remove a favorited item from your profile.</p>
            </div>
          </div>
        </div>
        : null}

      {
        value === 0 &&
          currentUser !== "Guest" &&
          favorites.length > 0 ?
          <div className="filter-options favorite-table">{renderFavorites()}</div>
          : <div className="filter-options">{renderBlank(random, currentUser)}</div>
      }
    </div>
  </main>

}

export default Favorites;